import { memo } from "react"

import { cn } from "@productlane/lib/src/browser"
import { Tooltip } from "@productlane/ui"
import { HighPriorityIcon } from "@productlane/ui/src/icons/issue-status/high-priority-icon"
import { LowPriorityIcon } from "@productlane/ui/src/icons/issue-status/low-priority-icon"
import { MediumPriorityIcon } from "@productlane/ui/src/icons/issue-status/medium-priority-icon"

export type RecommendationSeverity = "high" | "medium" | "low"

// About one chat a working day is high; one a week is medium.
const HIGH_FROM = 20
const MEDIUM_FROM = 5

export function recommendationSeverity(
	impactPerMonth: number,
): RecommendationSeverity {
	if (impactPerMonth >= HIGH_FROM) return "high"
	if (impactPerMonth >= MEDIUM_FROM) return "medium"
	return "low"
}

const ICONS = {
	high: HighPriorityIcon,
	medium: MediumPriorityIcon,
	low: LowPriorityIcon,
}

const LABELS: Record<RecommendationSeverity, string> = {
	high: "High",
	medium: "Medium",
	low: "Low",
}

export function severityTooltip(impactPerMonth: number): string {
	const severity = LABELS[recommendationSeverity(impactPerMonth)]
	return impactPerMonth === 1
		? `${severity} · about 1 chat a month`
		: `${severity} · about ${impactPerMonth} chats a month`
}

/** Linear's priority bars, sized by the chats a month a change would resolve. */
export const SeverityIcon = memo(
	({
		impactPerMonth,
		className,
	}: {
		impactPerMonth: number
		className?: string
	}) => {
		const Icon = ICONS[recommendationSeverity(impactPerMonth)]
		return (
			<Tooltip label={severityTooltip(impactPerMonth)} side="top">
				<span
					data-testid="agent-severity"
					className={cn("flex flex-none items-center", className)}
				>
					<Icon className="size-3.5 flex-none transform-gpu" />
				</span>
			</Tooltip>
		)
	},
)
